import { Button } from '@base-ui/react/button';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_LABELS } from '@/admin/languages';
import { PROFILE_TYPE_LABELS } from '@/admin/sitecontent/conditions';
import { MutationError } from '@/admin/sitecontent/errors';
import { invalidateNotices } from '@/admin/sitecontent/invalidate';
import { NoticeForm } from '@/admin/sitecontent/NoticeForm';
import { NoticeStateBadge } from '@/admin/sitecontent/NoticeStateBadge';
import {
  HeaderActions,
  HeaderRow,
  StatusMessage,
} from '@/admin/sitecontent/StatusMessage';
import buttonStyles from '@/admin/styles/button.module.css';
import { ConfirmDialog } from '@/admin/ui/ConfirmDialog';
import { DetailPage } from '@/admin/ui/Editor';
import { useEditMode, validateEditSearch } from '@/admin/ui/editSearch';
import { Field, FieldLabel, FieldValue } from '@/admin/ui/FieldDisplay';
import { BackLink } from '@/admin/ui/NavLinks';
import { PageTitle } from '@/admin/ui/Page';
import { RichContent } from '@/admin/ui/RichContent';
import { FieldGrid, Section, SectionTitle } from '@/admin/ui/Section';
import api from '@/api';

export const Route = createFileRoute('/admin/notices/$noticeId')({
  staticData: { minTier: 'SUPPORT' },
  validateSearch: validateEditSearch,
  component: NoticeDetail,
});

function NoticeDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { noticeId } = Route.useParams();
  const { edit } = Route.useSearch();
  const { editing, startEditing, stopEditing } = useEditMode(edit === true);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const notice = api.getAdminHeaderNotice.useQuery({
    path: { notice_id: Number(noticeId) },
  });
  const remove = api.deleteAdminHeaderNotice.useMutation(undefined, {
    onSuccess: () => {
      invalidateNotices();
      navigate({ to: '/admin/notices/' });
    },
  });

  if (notice.isLoading)
    return <StatusMessage>{t('admin.notices.loadingOne')}</StatusMessage>;
  if (notice.error || !notice.data)
    return (
      <StatusMessage $error>{t('admin.notices.errorLoadingOne')}</StatusMessage>
    );
  const data = notice.data;
  const when = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString() : t('admin.notices.never');

  return (
    <DetailPage>
      <BackLink>
        <Link to="/admin/notices/">{t('admin.notices.backToNotices')}</Link>
      </BackLink>
      <HeaderRow>
        <PageTitle>
          {data.title ||
            t('admin.notices.noticeN', {
              id: data.id,
              interpolation: { escapeValue: false },
            })}
        </PageTitle>
        {!editing && (
          <HeaderActions>
            <Button className={buttonStyles.Button} onClick={startEditing}>
              {t('admin.common.edit')}
            </Button>
            <Button
              className={buttonStyles.Button}
              onClick={() => setConfirmingDelete(true)}
            >
              {t('admin.common.delete')}
            </Button>
          </HeaderActions>
        )}
      </HeaderRow>

      {editing ? (
        <NoticeForm
          // Keyed on identity only: a background refetch must not drop unsaved
          // edits. A save leaves edit mode, so the next edit mounts fresh.
          key={data.id}
          notice={data}
          onSaved={stopEditing}
          onCancel={stopEditing}
        />
      ) : (
        <>
          <Section>
            <SectionTitle>{t('admin.common.details')}</SectionTitle>
            <FieldGrid>
              <Field>
                <FieldLabel>{t('admin.common.id')}</FieldLabel>
                <FieldValue>{data.id}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.language')}</FieldLabel>
                <FieldValue>{t(LANGUAGE_LABELS[data.language])}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.notices.col.state')}</FieldLabel>
                <FieldValue>
                  <NoticeStateBadge state={data.state} />
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.notices.starts')}</FieldLabel>
                <FieldValue>{when(data.starts_at)}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.notices.expires')}</FieldLabel>
                <FieldValue>{when(data.expires_at)}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.notices.audience')}</FieldLabel>
                <FieldValue>
                  {data.audience_profile_types.length === 0
                    ? t('admin.notices.everyone')
                    : data.audience_profile_types
                        .map((type) => t(PROFILE_TYPE_LABELS[type]))
                        .join(', ')}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.notices.col.dismissals')}</FieldLabel>
                <FieldValue>{data.dismissal_count}</FieldValue>
              </Field>
            </FieldGrid>
          </Section>
          <Section>
            <SectionTitle>{t('admin.notices.body')}</SectionTitle>
            {/* biome-ignore lint/security/noDangerouslySetInnerHtml: admin view of backend-authored notice HTML */}
            <RichContent dangerouslySetInnerHTML={{ __html: data.content }} />
          </Section>
        </>
      )}

      <ConfirmDialog
        open={confirmingDelete}
        title={t('admin.notices.deleteTitle')}
        confirmLabel={t('admin.common.delete')}
        tone="danger"
        pending={remove.isPending}
        onConfirm={() => remove.mutate({ path: { notice_id: data.id } })}
        onCancel={() => setConfirmingDelete(false)}
      >
        <p>
          {t('admin.notices.deleteBody', {
            dismissals: data.dismissal_count,
            interpolation: { escapeValue: false },
          })}
        </p>
        <MutationError area="notices" error={remove.error} />
      </ConfirmDialog>
    </DetailPage>
  );
}
